// Pure text-overlay math for the design-PICKER cover thumbnails
// (StepDesign → PickerCoverImage in AlbumFlowPage.tsx).
//
// The picker deliberately does NOT mount the live <CoverThumbnail> (that
// component runs a ResizeObserver + a per-zone text-measuring reflow +
// drag handlers on every card, which fought the scroll thread — see the
// "scroll fix" note in AlbumFlowPage). To still show the destination/year
// over the design we render a *static* CSS overlay whose font-size is
// expressed in container-query units (cqw), so it scales with the card
// width WITHOUT any JS during scroll.
//
// This module holds the only logic worth testing: resolving each zone's
// text + geometry + font-size. It mirrors CoverThumbnail's TextOverlay so
// the picker preview matches what the editor will render.

import type { TextZone } from '@/lib/api';
import { toCssFontFamily } from '@/lib/coverFonts';

// MUST equal PDF_WIDTH in CoverThumbnail.tsx. The editor sizes cover text as
// `font_size * (containerWidth / PDF_WIDTH)` px. Expressed as a fraction of
// container width that is `font_size / PDF_WIDTH`, i.e. cqw units, which is
// exactly what the CSS overlay below uses. Keep these two constants in sync.
export const PICKER_PDF_WIDTH = 1860;

export interface PickerZoneStyle {
  text: string;
  /** Position + size as percentages of the cover face. */
  leftPct: number;
  topPct: number;
  widthPct: number;
  heightPct: number;
  /** Font size in container-query-width units (1cqw = 1% of card width). */
  fontSizeCqw: number;
  color: string;
  /** Ready-to-use CSS font-family string. */
  fontFamily: string;
  justify: 'flex-start' | 'center' | 'flex-end';
  textAlign: 'left' | 'center' | 'right';
}

/**
 * Resolve the string a picker zone should show. Destination/year zones use
 * the customer's typed value, falling back to the template `default_text`
 * when the customer hasn't typed one yet (so the thumbnail is never blank).
 * Custom zones show their `default_text`. Returns '' when nothing resolves.
 */
export function pickerZoneText(
  zone: Pick<TextZone, 'type' | 'default_text'>,
  destination: string,
  year: string,
): string {
  const fallback = zone.default_text?.trim() || '';
  if (zone.type === 'destination') return destination.trim() || fallback;
  if (zone.type === 'year') return year.trim() || fallback;
  return fallback;
}

// Character-width-to-font-size ratio used to PREDICT how wide a string will
// render and decide whether to shrink it. Display fonts vary widely:
// Anton/condensed ≈ 0.45, Londrina Solid ≈ 0.6, Bungee/wide ≈ 0.8+. We pick
// the wide-end value so the predictor over-shrinks rather than under-shrinks
// — short text is unaffected (the formula short-circuits when est < zone),
// but long text in any font is guaranteed to fit. Was 0.55, but real covers
// with display fonts (AGADIR / MEMORIES / LOVE YOU) still clipped because
// 0.55 wildly underestimated their actual rendered width.
const AVG_CHAR_WIDTH_RATIO = 0.9;
// Use 95% of the zone as the budget — matches CoverThumbnail's `usableWidth =
// boxWidth * 0.95` (web/src/components/CoverThumbnail.tsx:647).
const ZONE_USABLE_FRACTION = 0.95;
// CoverThumbnail floors its autoScale at 0.3 so text never disappears entirely
// in pathological cases — mirror that here.
const MIN_AUTO_SCALE = 0.3;

/**
 * Shrink a font size (in cqw) so the longest word/line fits the zone width,
 * matching CoverThumbnail's JS measure-and-scale behaviour with a font-metric
 * heuristic. Returns the original size when text already fits.
 */
function fitFontSizeCqw(rawCqw: number, text: string, zoneWidthFrac: number): number {
  if (!text) return rawCqw;
  // Longest individual line — text may contain hard newlines.
  const longest = text.split('\n').reduce((a, b) => (a.length >= b.length ? a : b), '');
  if (!longest) return rawCqw;
  const zoneWidthCqw = zoneWidthFrac * 100;
  const available = zoneWidthCqw * ZONE_USABLE_FRACTION;
  const estimatedWidth = longest.length * rawCqw * AVG_CHAR_WIDTH_RATIO;
  if (estimatedWidth <= available) return rawCqw;
  const shrinkFactor = Math.max(available / estimatedWidth, MIN_AUTO_SCALE);
  return rawCqw * shrinkFactor;
}

/**
 * Build the static CSS overlay style for one picker zone, or null when the
 * zone has no text to show. Geometry/colour/font mirror CoverThumbnail's
 * TextOverlay; font-size is emitted in cqw so it tracks the card width with
 * zero JS (no ResizeObserver, no measuring) — the scroll fix stays intact.
 */
export function pickerZoneStyle(
  zone: TextZone,
  destination: string,
  year: string,
): PickerZoneStyle | null {
  const text = pickerZoneText(zone, destination, year);
  if (!text) return null;

  const baseFontSize = zone.font_size ?? 32;
  const align = zone.align ?? 'center';
  const rawCqw = (baseFontSize / PICKER_PDF_WIDTH) * 100;

  return {
    text,
    leftPct: zone.x * 100,
    topPct: zone.y * 100,
    widthPct: zone.width * 100,
    heightPct: zone.height * 100,
    fontSizeCqw: fitFontSizeCqw(rawCqw, text, zone.width),
    color: zone.color ?? '#ffffff',
    fontFamily: toCssFontFamily(zone.font_family ?? null),
    justify: align === 'left' ? 'flex-start' : align === 'right' ? 'flex-end' : 'center',
    textAlign: align,
  };
}

/**
 * C2 — font-load specs for the picker's text overlays.
 *
 * Why: every cover font is declared in index.html with display=swap, but the
 * browser only DOWNLOADS a webfont when rendered text first uses it — and the
 * picker IS that first use. So the first paint always measures with a
 * fallback font, and the text visibly re-lays-out ("snaps") whenever the real
 * font arrives. The picker overlay renders at font-weight 400, so one
 * `document.fonts.load('400 16px "<family>"')` per distinct family is exactly
 * what must complete before the text is shown.
 *
 * Returns deduped load-spec strings for the families these zones use.
 */
export function pickerFontLoadSpecs(zones: Pick<TextZone, 'font_family'>[]): string[] {
  const families = new Set<string>();
  for (const z of zones) {
    // First token of the CSS stack is the real webfont (rest are fallbacks).
    const first = toCssFontFamily(z.font_family ?? null)
      .split(',')[0]!
      .trim()
      .replace(/^['"]|['"]$/g, '');
    if (first) families.add(first);
  }
  return Array.from(families).map((f) => `400 16px "${f}"`);
}

/**
 * Resolve ALL of a cover's picker zones, then HORIZONTALLY CENTER them for the
 * grid (picker-only). The catalogue's stored zone.x drifts per cover — some
 * titles/years sit left, some right — so the grid never lines up. We fix that
 * here without touching the saved cover (the editor + PDF keep the real zone.x
 * via CoverThumbnail).
 *
 * Centering is done PER LINE, not per zone: zones whose vertical ranges overlap
 * (e.g. an inline "GENEVA 2026") are grouped and the whole group is shifted so
 * its combined box is centered — preserving the intra-line layout so the name
 * and year never collide. A line with a single zone is centered outright (its
 * text forced center too). Vertical position (zone.y) is always preserved.
 */
export function pickerZoneStyles(
  zones: TextZone[],
  destination: string,
  year: string,
): PickerZoneStyle[] {
  const items = zones
    .map((z) => {
      const s = pickerZoneStyle(z, destination, year);
      return s ? { s, top: z.y, bottom: z.y + z.height } : null;
    })
    .filter((it): it is { s: PickerZoneStyle; top: number; bottom: number } => it !== null);
  if (items.length === 0) return [];

  // Group by vertical overlap (same line). Sort a copy by top edge; a zone joins
  // the current group when it starts before the group's running bottom.
  const sorted = [...items].sort((a, b) => a.top - b.top);
  const groups: (typeof sorted)[] = [];
  let bottom = -Infinity;
  for (const it of sorted) {
    if (groups.length === 0 || it.top >= bottom) {
      groups.push([it]);
      bottom = it.bottom;
    } else {
      groups[groups.length - 1].push(it);
      bottom = Math.max(bottom, it.bottom);
    }
  }

  // Shift each group so its combined horizontal box is centered on the card.
  for (const group of groups) {
    let left = Infinity;
    let right = -Infinity;
    for (const it of group) {
      left = Math.min(left, it.s.leftPct);
      right = Math.max(right, it.s.leftPct + it.s.widthPct);
    }
    const dx = 50 - (left + right) / 2;
    const single = group.length === 1;
    for (const it of group) {
      it.s.leftPct += dx;
      // A lone title on its line: also force its text centered so short and long
      // names alike sit dead-center. Multi-zone lines keep each zone's own align
      // so the inline layout (e.g. name left, year right) is preserved.
      if (single) {
        it.s.justify = 'center';
        it.s.textAlign = 'center';
      }
    }
  }

  return items.map((it) => it.s);
}
